//src/hooks/useFontSize.js

import { useState, useEffect } from "react";

const DEFAULT_PERCENT = 100;
const MIN_PERCENT = DEFAULT_PERCENT;
const MAX_PERCENT = 150;
const STEP_PERCENT = 12.5; // 16px -> 18px -> 20px ... at a 16px browser default

const STORAGE_KEY = "fontSizePercent";
// Previous implementation stored an absolute px value under this key and
// forced it (default 18px) on every user. Only genuine increases are migrated.
const LEGACY_STORAGE_KEY = "fontSize";
const LEGACY_DEFAULT_PX = 18;

function readInitialPercent() {
  try {
    const saved = parseFloat(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(saved)) {
      return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, saved));
    }
    const legacyPx = parseFloat(localStorage.getItem(LEGACY_STORAGE_KEY));
    if (Number.isFinite(legacyPx) && legacyPx > LEGACY_DEFAULT_PX) {
      return Math.min(MAX_PERCENT, (legacyPx / 16) * 100);
    }
  } catch {
    /* storage unavailable – fall through to the default */
  }
  return DEFAULT_PERCENT;
}

export function useFontSize() {
  const [fontSize, setFontSize] = useState(readInitialPercent);

  useEffect(() => {
    const root = document.documentElement;
    if (fontSize === DEFAULT_PERCENT) {
      // Leave the browser's own default untouched.
      root.style.removeProperty("font-size");
    } else {
      root.style.fontSize = `${fontSize}%`;
    }
    try {
      localStorage.setItem(STORAGE_KEY, String(fontSize));
    } catch {
      /* ignore */
    }
  }, [fontSize]);

  const increaseFont = () =>
    setFontSize((prev) => Math.min(prev + STEP_PERCENT, MAX_PERCENT));

  const decreaseFont = () =>
    setFontSize((prev) => Math.max(prev - STEP_PERCENT, MIN_PERCENT));

  const resetFont = () => setFontSize(DEFAULT_PERCENT);

  return {
    fontSize,
    increaseFont,
    decreaseFont,
    resetFont,
  };
}
